import { formatToBRL } from "@/utils/formatToMoney";

import {
  PayrollDiscountCandidate,
  isPayrollDiscount,
} from "./is-payroll-discount";

export type ReversibleAmount = number | string;

export interface ReversibleInstallment {
  number: number;
  amount: ReversibleAmount;
  competence: string;
  sent?: boolean;
}

export interface ReversibleDivergence extends PayrollDiscountCandidate {
  store_code: number | string;
  amount: ReversibleAmount;
  installments: readonly ReversibleInstallment[];
}

const amountOf = (value: ReversibleAmount): number => Number(value);

export interface ReversalEffect {
  credited_amount: number;
  cancelled_amount: number;
  charged_count: number;
  pending_count: number;
  registers_loss: boolean;
  loss_amount: number;
  allowed_in_batch: boolean;
  statement: string;
}

const sumOf = (installments: readonly ReversibleInstallment[]): number =>
  installments.reduce(
    (total: number, installment: ReversibleInstallment): number =>
      total + amountOf(installment.amount),
    0,
  );

const openingSentence = (
  total: number,
  installments: readonly ReversibleInstallment[],
): string => {
  if (installments.length === 0) {
    return "Este caso não tem vale — nada foi parcelado nem cobrado do colaborador.";
  }

  if (installments.length === 1) {
    return `Este vale foi aprovado em parcela única de ${formatToBRL(total)}.`;
  }

  return `Este vale foi aprovado em ${installments.length} parcelas, somando ${formatToBRL(total)}.`;
};

const creditSentence = (credited: number, chargedCount: number): string => {
  if (chargedCount === 0) {
    return "Nenhuma parcela foi cobrada ainda, então nada será creditado ao colaborador.";
  }

  const parcels: string =
    chargedCount === 1
      ? "1 parcela já foi cobrada"
      : `${chargedCount} parcelas já foram cobradas`;

  return `${parcels}. A reversão irá creditar ${formatToBRL(credited)} ao colaborador.`;
};

const cancelSentence = (cancelled: number, pendingCount: number): string => {
  if (pendingCount === 0) return "Não há parcela pendente para cancelar.";

  const parcels: string =
    pendingCount === 1
      ? "Será cancelada 1 parcela restante"
      : `Serão canceladas as ${pendingCount} parcelas restantes`;

  return `${parcels} (${formatToBRL(cancelled)}).`;
};

const bookSentence = (
  divergence: ReversibleDivergence,
  credited: number,
  registersLoss: boolean,
): string => {
  if (!registersLoss) {
    return "Nada foi cobrado do colaborador, então a reversão cancela o vale sem gerar Perda e o caso volta para a fila para você decidir de novo.";
  }

  const loss = `Uma Perda de ${formatToBRL(divergence.amount)} será registrada na Loja ${divergence.store_code} no ciclo aberto.`;

  if (credited === 0) return loss;

  const creditBook: string = isPayrollDiscount(divergence)
    ? `O crédito de ${formatToBRL(credited)} entra na próxima planilha do Sênior.`
    : `O crédito de ${formatToBRL(credited)} entra na planilha do Contábil.`;

  return `${creditBook} ${loss}`;
};

export const reversalEffectOf = (
  divergence: ReversibleDivergence,
  openCycle: string,
): ReversalEffect => {
  const charged: ReversibleInstallment[] = divergence.installments.filter(
    (installment: ReversibleInstallment): boolean =>
      installment.competence < openCycle,
  );

  const pending: ReversibleInstallment[] = divergence.installments.filter(
    (installment: ReversibleInstallment): boolean =>
      installment.competence >= openCycle,
  );

  const credited: number = sumOf(charged);
  const cancelled: number = sumOf(pending);

  // Sem parcela cobrada a reversão devolve o caso à fila (returnsToQueue = chargedCount === 0),
  // e a empresa não absorve nada — anunciar Perda aqui espantaria o Gerente do reparcelamento (D21.2).
  const registersLoss: boolean = charged.length > 0;

  return {
    credited_amount: credited,
    cancelled_amount: cancelled,
    charged_count: charged.length,
    pending_count: pending.length,
    registers_loss: registersLoss,
    loss_amount: amountOf(divergence.amount),
    allowed_in_batch: charged.length === 0,
    statement: [
      openingSentence(credited + cancelled, divergence.installments),
      creditSentence(credited, charged.length),
      cancelSentence(cancelled, pending.length),
      bookSentence(divergence, credited, registersLoss),
    ].join(" "),
  };
};

export interface BatchReversalSplit {
  reversible: readonly ReversibleDivergence[];
  requiresIndividual: readonly ReversibleDivergence[];
  total_to_cancel: number;
}

export const splitBatchReversal = (
  divergences: readonly ReversibleDivergence[],
  openCycle: string,
): BatchReversalSplit => {
  const reversible: ReversibleDivergence[] = divergences.filter(
    (divergence: ReversibleDivergence): boolean =>
      reversalEffectOf(divergence, openCycle).allowed_in_batch,
  );

  return {
    reversible,
    requiresIndividual: divergences.filter(
      (divergence: ReversibleDivergence): boolean =>
        !reversalEffectOf(divergence, openCycle).allowed_in_batch,
    ),
    total_to_cancel: reversible.reduce(
      (total: number, divergence: ReversibleDivergence) =>
        total + reversalEffectOf(divergence, openCycle).cancelled_amount,
      0,
    ),
  };
};
